import { CommonModule, KeyValue } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { saveAs } from 'file-saver';
import { LazyLoadEvent } from 'primeng/api';
import { finalize, map, Observable, Subject, takeUntil, tap } from 'rxjs';
import { AppService } from 'src/app/services/app.service';
import { MessageToUiService } from 'src/app/services/message-to-ui.service';
import { GlobalSpinnerService } from 'src/app/shared/global-spinner/global-spinner.service';
import { LocalizationService, LocalizedNumberFormat } from 'src/app/shared/localization/localization.service';
import { DebtorScoreColor, ICasesScoreDetailsRequest, ICasesScoreRequest, ICustomerCasesScoreDetail, ICustomerCasesScoreSummary, SCORE_REPORT_DETAILS_COLUMNS, SCORE_REPORT_RESULT_COLUMNS } from '../reports-models';
import { ReportsService } from '../reports.service';
import { CustomMultiSelectComponent } from 'src/app/shared/custom-multi-select/custom-multi-select.component';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { SettingsService } from "src/app/services/settings.service";

@Component({
  selector: 'app-score',
  templateUrl: './score.component.html',
  styleUrls: ['./score.component.scss'],
  imports: [CommonModule, TranslateModule, CustomMultiSelectComponent, TableModule, ButtonModule]
})
export class ScoreComponent implements OnInit {
  customerNumber$ = this.appService.customerNumber$;
  customers$: Observable<KeyValue<number, string>[]>;
  selectedCustomers: number[] = [];
  scoresSummary: ICustomerCasesScoreSummary[];

  numberOfRowsPerPage = 20;
  currentPageNumber = 1;
  totalNumberOfItems = 0;
  userAssignedCustomersCount : number;

  numberFormat: LocalizedNumberFormat;
  columns = SCORE_REPORT_RESULT_COLUMNS;
  subColumns = [...SCORE_REPORT_DETAILS_COLUMNS];

  canSearch: boolean;
  showResults: boolean;
  isLoading: boolean;

  private readonly unsubscribe$ = new Subject();


  constructor(
    private appService: AppService,
    private reportsService: ReportsService,
    private notifications: MessageToUiService,
    private spinnerService: GlobalSpinnerService,
    private localisationService: LocalizationService,
    private translateService: TranslateService,
    private settingsService: SettingsService,
    private router: Router
  ) {

  }
  ngOnInit(): void {
    this.spinnerService.show();
    this.initializeCustomerList();
    this.localisationService.settings$.subscribe(
      (_) => (this.numberFormat = _.numberPipeFormat)
    );

    this.settingsService.getSettingValueByName('AreRef2Ref3Inlcuded')
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((areRef2Ref3Inlcuded) => {
        if (!areRef2Ref3Inlcuded.isTruthy()) {
          this.subColumns = this.subColumns.filter(
            col => col.name !== 'customerRef2' && col.name !== 'customerRef3'
          );
        }
      });
    
    this.spinnerService.hide();
  }

  private initializeCustomerList() {
    this.customers$ = this.appService.customers$.pipe(
      tap(_ => {
        this.userAssignedCustomersCount = _.length;
        if (this.userAssignedCustomersCount == 1)
          {this.selectedCustomers = [_[0].number]; }
      }),
      map((_) => _.map((x) => ({ key: x.number, value: `${x.number} - ${x.name}` })))
    );
  }

  public onSelectedCustomersChange(event: number[]) {
    if (event.length == 0) {
      this.scoresSummary = null;
    }
  }

  onGenerateReport() {
    if (this.selectedCustomers?.length == 0) {
      this.notifications.errorKey('Errors.Reports.NoCustomersSelected');
      return false;
    }
    const parameters = this.getReportParameters();
    this.isLoading = true;
    this.reportsService
      .getScoreReport(parameters)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe((_) => {
        this.scoresSummary = _;
      });
  }

  getDetails(score: DebtorScoreColor) {
    const detailsParams = this.getReportDetailsParameters(score);
    const summary = this.scoresSummary.find(x => x.score == score);
    if (summary.cases == undefined) {
      this.spinnerService.show();
      this.reportsService
        .getScoreDetails(detailsParams)
        .pipe(finalize(() => {
          this.isLoading = false;
          this.spinnerService.hide();
        }))
        .subscribe((_) => {
          this.scoresSummary.filter(x => x.score == score)[0].cases = _;
        });
    }
  }

  onPageChange(event: LazyLoadEvent) {
    if (event.rows != this.numberOfRowsPerPage)
      {this.numberOfRowsPerPage = event.rows; }
    this.currentPageNumber =
      event.first == 0 ? 1 : event.first / event.rows + 1;
  }

  onOpenCase(selectedCase: ICustomerCasesScoreDetail) {
    if (selectedCase != null) {
      this.router.navigate([]).then(result => { window.open(`cases/${selectedCase.caseNumber}`, '_blank'); });
    }
  }

  getReportParameters(): ICasesScoreRequest {
    return {
      customerNumbers: this.selectedCustomers
    } as ICasesScoreRequest;
  }

  getReportDetailsParameters(score: DebtorScoreColor): ICasesScoreDetailsRequest {
    return {
      customerNumbers: this.selectedCustomers,
      score
    } as ICasesScoreDetailsRequest;
  }

  onExportExcelClick() {
    if (this.selectedCustomers?.length == 0) {
      this.notifications.errorKey('Errors.Reports.NoCustomersSelected');
      return false;
    }
    const parameters = this.getReportParameters();
    this.isLoading = true;
    this.reportsService.exportScoreReportForExcel(parameters, this.translateService.getDefaultLang())
      .pipe(finalize(() => { this.isLoading = false; }), takeUntil(this.unsubscribe$))
      .subscribe(
        blob => {
          saveAs(blob, `${this.translateService.instant('Reports.Score.Title')}_${new Date().getDate() + (new Date().getMonth() + 1) + new Date().getFullYear() + '_' + new Date().getTime().toString().slice(0, 6)}.xlsx`);
        },
        error => {
          this.notifications.genericError();
        });
  }
}
